import {default as React, useEffect, useState} from 'react';
import {NavLink} from 'react-router-dom';
import {Breadcrumbs} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {Trash2} from "lucide";
import {LucideIcon} from "~/components/LucideIcon";
import {useConfirm} from '~/components/Dialogs';
import {onSubmitError} from '~/components/Form';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {useDataSource} from '~/lib/dataSource';
import {Intent} from '~/lib/intent';
import {sortByNumber, sortByString} from '~/lib/sort';
import {SupplyPackagesAbility} from '~/modules/Pmp/ability';
import {supplyPackagesAdapter} from '~/modules/Pmp/adapters';
import {deleteSupplyPackage} from '~/modules/Pmp/api';
import {SupplyPackageForm} from '~/modules/Pmp/forms/supplyPackageForm';
import {defaultSupplyPackage} from '~/modules/Pmp/models/SupplyPackage';
import {Input} from '~/components/Controls/Input';
import {PmpNavigation} from '~/modules/Pmp/components/PmpNavigation';
import {pmpLinks} from '~/modules/Pmp';
import style from './Pmp.module.scss';

export const SupplyPackagesView = () => {
    const ability = useAbility(SupplyPackagesAbility);
    let {state, fetchRecords, createRecord, updateRecord} = useDataSource(supplyPackagesAdapter);
    useEffect(() => fetchRecords(), []);

    const modals = useModals();
    const confirm = useConfirm();

    const createSupplyPackage = modals.create(SupplyPackageForm, {
        onSubmit: (newSupplyPackage, formikActions, close) => {
            createRecord(newSupplyPackage).then(close).catch(onSubmitError(formikActions));
        }
    });

    const editSupplyPackage = modals.create(SupplyPackageForm, {
        onSubmit: (updatedSupplyPackage, formikActions, close) => {
            updateRecord(updatedSupplyPackage).then(close).catch(onSubmitError(formikActions));
        }
    });

    const [filter, setFilter] = useState({
        search: ''
    });

    const setSearchFilter = (value: string) => {
        setFilter({...filter, search: value});
    };

    const recordsFilter = (record: any) => {
        if (!filter.search) {
            return record;
        }

        if (record.name.toLowerCase().includes(filter.search.toLowerCase())) {
            return record;
        }

        if (record.description.toLowerCase().includes(filter.search.toLowerCase())) {
            return record;
        }

        return;
    };

    return <div>
        <div className="page-header-sticky">
            <div className="page-header-breadcrumbs">
                <Breadcrumbs/>
            </div>
            <Title className={`${style.supplyPackagesList} page-header-title`} toolbar={<>
            {ability.canCreate() && <Button intent={Intent.PRIMARY}
                responsive
                disabled={state.isPending}
                onClick={() => createSupplyPackage.show({supplyPackage: defaultSupplyPackage})}
                leftIcon={<i className='fa fa-plus'/>}
            >Add package</Button>}
            </>}>Supply Packages</Title>
        </div>
        <PmpNavigation/>
        <div className="layout-content-box">
            <div>
                <form className={style.filter} onSubmit={(e) => e.preventDefault()}>
                    <Input
                        value={filter.search}
                        onChange={setSearchFilter}
                        placeholder='Search'
                        className={style.searchInput}
                        rightIcon={<i className='fa fa-search input-search-icon' />}
                    />
                </form>
            </div>
        <TableSort
            records={state.records.filter(recordsFilter)}
            defaultSort={{
                sort: sortByNumber,
                field: 'id',
                direction: TableSortDirection.DESC
            }}
            header
            columns={[
                {
                    header: 'ID',
                    small: true,
                    field: 'id',
                    sort: sortByNumber
                }, {
                    header: 'Name',
                    field: 'name',
                    cell: (supplyPackage) =><div className={style.ellipsisMulti}> <NavLink to={pmpLinks.supplyPackage(supplyPackage.id)}>{supplyPackage.name}</NavLink></div>,
                    sort: sortByString,
                },  {
                    header: 'Description',
                    field: 'description',
                    cell: supplyPackage =><div className={style.ellipsisMulti}>{supplyPackage.description}</div>,
                    sort: sortByString,
                },  {
                    small: true,
                    cell: (supplyPackage) => {
                        const editButton = ability.canEdit() && (
                            <Button
                                small
                                variant="menu"
                                leftIcon={<i className='fa fa-edit'/>}
                                onClick={() => editSupplyPackage.show({supplyPackage})}
                            >
                                Edit
                            </Button>
                        );
                        const deleteButton = ability.canDelete() && (
                            <Button
                                small
                                variant="menu"
                                intent={Intent.DANGER}
                                leftIcon={<i className='fa fa-trash'/>}
                                onClick={() => confirm('Delete Supply Package?', {
                                    onOk: () => deleteSupplyPackage(supplyPackage.id).then(fetchRecords),
                                    icon: <LucideIcon icon={Trash2} size={20} />,
                                    intent: Intent.DANGER
                                })}
                            >
                                Delete
                            </Button>
                        );
                        return <TableRowActions>{editButton}{deleteButton}</TableRowActions>;
                    }
                }
            ]}
            placeholder={state.isPending && <Progress/>}/>
        </div>
    </div>;
};
